import type { PaletteId } from '../../ui/settings';
import { COLORS, LUMINOSITY_SHADES } from './constants';
import { CLASSIC_ODDS, type OddsColors } from './odds';

/**
 * The colours a player must tell apart at a glance: the squares, the cycling squares as the
 * luminosity turns, the rings under pieces and squares, and the two classic sides. The classic
 * palette is the original look; the colour-blind one keeps every pair apart by brightness or by
 * a blue/orange contrast that deutan and protan vision both preserve.
 */
export interface Palette {
  id: PaletteId;
  /** Fixed light and dark squares, and the cycling squares before the first turn of the cycle. */
  tiles: { light: string; dark: string; cycling: string };
  /** Cycling squares for luminosity phases 0 (darkest) … 5 (brightest); brightness rises with the phase. */
  luminosityShades: readonly string[];
  highlights: { move: string; attack: string; spell: string; lastMove: string };
  /** The ramp an attack target's odds are drawn in, its ring and its label. */
  odds: OddsColors;
  /** Colours that replace the board data's, by faction id; a faction not listed keeps its own. */
  factions: Readonly<Record<string, string>>;
}

export const PALETTES: Record<PaletteId, Palette> = {
  classic: {
    id: 'classic',
    tiles: { light: COLORS.light, dark: COLORS.dark, cycling: COLORS.cycling },
    luminosityShades: LUMINOSITY_SHADES,
    highlights: {
      move: COLORS.highlightMove,
      attack: COLORS.highlightAttack,
      spell: COLORS.highlightSpell,
      lastMove: COLORS.highlightLastMove,
    },
    odds: CLASSIC_ODDS,
    factions: {},
  },
  'colour-blind': {
    id: 'colour-blind',
    // Pale straw against deep navy: the squares differ in brightness as much as in hue, and the
    // cycle climbs through blue-greys instead of purples so every phase is a step brighter.
    tiles: { light: '#f3e6a6', dark: '#1a2d6d', cycling: '#4f7bb7' },
    luminosityShades: ['#101c45', '#1a2d6d', '#4f7bb7', '#8ea3c4', '#cfcaa9', '#f3e6a6'],
    // Moves in sky blue, attacks in orange: the pair that survives red/green confusion.
    // The last move in white, apart from every ring's hue.
    highlights: { move: '#4fc3ff', attack: '#ff9f1c', spell: '#cc79a7', lastMove: '#ffffff' },
    // Poor odds orange, even near white, good sky blue: red against green was the pair a deutan
    // eye could not tell apart, and only the small percentage told them (client-board:quality#9).
    odds: {
      poor: { r: 1, g: 0.62, b: 0.11 },
      even: { r: 0.96, g: 0.96, b: 0.96 },
      good: { r: 0.31, g: 0.76, b: 1 },
    },
    // Light plays orange-gold, Dark plays blue; the sides also differ in brightness.
    factions: { light: '#f39c2a', dark: '#2a5fc4' },
  },
};

export function paletteFor(id: PaletteId): Palette {
  return PALETTES[id];
}

/** A faction's colour under a palette: the palette's stand-in, or the board data's own. */
export function factionColor(palette: Palette, faction: { id: string; color: string }): string {
  return palette.factions[faction.id] ?? faction.color;
}
